import { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import './styles.css';  // Import global CSS
import API_URL from './config.js';
import LoginView from './views/LoginView';
import { getToken, setToken as setAuthToken, applyAxiosAuth, authFetch, clearToken, getStoredUser, setStoredUser } from './auth';

// css
import './AppLayout.css';
import './components/ButtonStyles.css';

// UI Components
import TopHeader from './components/TopHeader';
import CabinetsModal from './components/CabinetsModal';
import SidebarMenu from './components/SidebarMenu';

// views
import UploadView from './views/UploadView';
import SearchView from './views/SearchView';
import ManageView from './views/ManageView';
import AllFilesView from './views/AllFilesView';
import UsersView from './views/UsersView';
import AdminStatusView from './views/AdminStatusView';
import AdminCabinetsView from './views/AdminCabinetsView';
import AdminBackupView from './views/AdminBackupView';
import TrashView from './views/TrashView';
import EmailModal from './components/EmailModal';
import NewBarcodeView from './views/NewBarcodeView';
import UserSettingsModal from './components/UserSettingsModal';

const DEFAULT_SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const TOKEN_REFRESH_MIN_HEADROOM_MS = 10000;
const TOKEN_REFRESH_MAX_HEADROOM_MS = 60 * 1000;

function App() {
  const [customerName, setCustomerName] = useState(() => {
    try { return localStorage.getItem('customerName') || ''; } catch (_) { return ''; }
  });
  const [cabinetFields, setCabinetFields] = useState([]);
  const [fieldKeys, setFieldKeys] = useState([]);
    const [file, setFile] = useState(null);
    const [files, setFiles] = useState([]);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [fileList, setFileList] = useState([]);
    const [view, setView] = useState(() => {
      try { return localStorage.getItem('app.view') || 'upload'; } catch (_) { return 'upload'; }
    });
    // Sort state for file listings (persisted in localStorage for All Files view)
    const [sortField, setSortField] = useState(() => {
      try { return localStorage.getItem('allFiles.sortField') || null; } catch (_) { return null; }
    });
    const [sortDirection, setSortDirection] = useState(() => {
      try { return localStorage.getItem('allFiles.sortDirection') || 'asc'; } catch (_) { return 'asc'; }
    });
    const [selectedRow, setSelectedRow] = useState(null);  // Track selected row
    const [searching, setSearching] = useState(false);
    const [searchCancelToken, setSearchCancelToken] = useState(null);
    const [searchSortField, setSearchSortField] = useState(null);
  const [searchSortDirection, setSearchSortDirection] = useState('asc');
  const [ocrStatus, setOcrStatus] = useState(null);
  const [auth, setAuth] = useState({ token: '', user: null });
  const sessionTimeoutMs = useMemo(() => {
    const rawMinutes = Number(auth?.user?.timeout);
    if (Number.isFinite(rawMinutes) && rawMinutes > 0) {
      return Math.max(60 * 1000, rawMinutes * 60 * 1000);
    }
    return DEFAULT_SESSION_TIMEOUT_MS;
  }, [auth?.user?.timeout]);
  // UI theme reflects the immediate, effective theme (optimistic toggle)
  // Initialize from localStorage to persist across refreshes before user data loads
  const [uiTheme, setUiTheme] = useState(() => {
    try {
      return (localStorage.getItem('theme') || 'light').toLowerCase();
    } catch (_) {
      return 'light';
    }
  });
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailFilename, setEmailFilename] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailBusyText, setEmailBusyText] = useState('Sending…');
  const [cabinetsOpen, setCabinetsOpen] = useState(false);
  const [cabinetSwitchingName, setCabinetSwitchingName] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const closeSettings = () => {
    if (settingsBusy) return;
    setSettingsOpen(false);
    setSettingsError('');
  };



  // Progress and upload state
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [cancelToken, setCancelToken] = useState(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadIndex, setUploadIndex] = useState(0);
  const [uploadTotal, setUploadTotal] = useState(0);
  const [uploadOverallProgress, setUploadOverallProgress] = useState(0);
  const [uploadEta, setUploadEta] = useState('');

  // Toast notifications
  const [toast, setToast] = useState({ open: false, message: '', type: 'info' });
  const toastRef = useRef(toast);
  useEffect(() => { toastRef.current = toast; }, [toast]);
  const showToast = (message, type = 'success', durationMs = 3000) => {
    const current = toastRef.current || {};
    // Dedupe identical, already-open toasts (prevents spam like repeated "Loading files…")
    if (current.open && current.message === message && current.type === type) {
      // If a finite duration is requested, refresh its timeout but do not re-render
      window.clearTimeout(showToast._t);
      if (durationMs > 0) {
        showToast._t = window.setTimeout(() => setToast(t => ({ ...t, open: false })), durationMs);
      }
      return;
    }
    setToast({ open: true, message, type });
    window.clearTimeout(showToast._t);
    if (durationMs > 0) {
      showToast._t = window.setTimeout(() => setToast(t => ({ ...t, open: false })), durationMs);
    }
  };
  // Expose toast for children that don't receive props
  useEffect(() => {
    try { window.spartaToast = showToast; } catch (_) {}
    return () => { try { delete window.spartaToast; } catch (_) {} };
  }, []);

  // Expose a global setter so admin pages can update the header customer name immediately (e.g., after rename)
  useEffect(() => {
    try { window.spartaSetCustomerName = (name) => setCustomerName(String(name || '')); } catch (_) {}
    return () => { try { delete window.spartaSetCustomerName; } catch (_) {} };
  }, []);

  // Greet user once per browser session (persist flag in sessionStorage) with a small delay
  let _initialGreeted = false;
  try { _initialGreeted = sessionStorage.getItem('sparta.greeted') === '1'; } catch (_) { /* ignore */ }
  const greetedRef = useRef(_initialGreeted);
  const greetTimerRef = useRef(null);
  const scheduleGreeting = () => {
    if (!auth?.user || greetedRef.current) return;
    window.clearTimeout(greetTimerRef.current);
    greetTimerRef.current = window.setTimeout(() => {
      try {
        if (greetedRef.current) return;
        const hour = new Date().getHours();
        const period = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
        const uname = (auth?.user && (`${auth.user.first_name || ''} ${auth.user.last_name || ''}`.trim() || auth.user.login)) || 'User';
        showToast(`Good ${period}, ${uname}`, 'info', 3000);
        greetedRef.current = true;
        try { sessionStorage.setItem('sparta.greeted', '1'); } catch (_) {}
      } catch (_) {}
    }, 1500); // allow any startup/progress toasts to show first
  };
  useEffect(() => { scheduleGreeting(); return () => window.clearTimeout(greetTimerRef.current); }, [auth?.user]);

  // --- Session timeout and auto-logout ---
  const authTokenRef = useRef('');
  const idleTimerRef = useRef(null);
  const expTimerRef = useRef(null);
  const activityBoundRef = useRef(false);
  const refreshInFlightRef = useRef(false);

  useEffect(() => {
    authTokenRef.current = auth.token || '';
  }, [auth.token]);

  function decodeJwtExp(token) {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const payload = parts[1]
        .replace(/-/g, '+')
        .replace(/_/g, '/');
      const json = JSON.parse(decodeURIComponent(atob(payload).split('').map(c =>
        '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      ).join('')));
      if (json && typeof json.exp === 'number') return json.exp * 1000; // ms
      return null;
    } catch (_) {
      return null;
    }
  }

  useEffect(() => {
    let cancelled = false;
    const clearTimers = () => {
      if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
      if (expTimerRef.current) { clearTimeout(expTimerRef.current); expTimerRef.current = null; }
    };
    const unbindActivity = () => {
      if (!activityBoundRef.current) return;
      activityBoundRef.current = false;
      const evs = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
      evs.forEach(ev => window.removeEventListener(ev, resetIdle));
    };

    const doLogout = (reason) => {
      if (cancelled) return;
      try { clearToken(); } catch (_) {}
      setAuth({ token: '', user: null });
      clearTimers();
      unbindActivity();
      if (reason === 'expired') {
        showToast('Session expired. Please sign in again.', 'info', 5000);
      } else if (reason === 'idle') {
        const minutes = Math.max(1, Math.round(sessionTimeoutMs / 60000));
        showToast(`Signed out after ${minutes} minute${minutes === 1 ? '' : 's'} of inactivity.`, 'info', 5000);
      }
    };

    const resetIdle = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => doLogout('idle'), sessionTimeoutMs);
    };

    function scheduleTokenRefresh(tokenOverride) {
      if (expTimerRef.current) {
        clearTimeout(expTimerRef.current);
        expTimerRef.current = null;
      }
      if (cancelled) return;
      const tokenToUse = tokenOverride || authTokenRef.current;
      if (!tokenToUse) return;
      const expMs = decodeJwtExp(tokenToUse);
      if (typeof expMs !== 'number') return;
      const now = Date.now();
      if (expMs <= now) {
        performTokenRefresh();
        return;
      }
      const remaining = expMs - now;
      const headroom = Math.min(
        Math.max(TOKEN_REFRESH_MIN_HEADROOM_MS, Math.floor(sessionTimeoutMs * 0.1)),
        TOKEN_REFRESH_MAX_HEADROOM_MS
      );
      const waitMs = remaining - headroom;
      if (waitMs <= 0) {
        performTokenRefresh();
      } else {
        expTimerRef.current = setTimeout(() => {
          performTokenRefresh();
        }, waitMs);
      }
    }

    async function performTokenRefresh() {
      if (cancelled || refreshInFlightRef.current) return;
      const activeToken = authTokenRef.current;
      if (!activeToken) return;
      refreshInFlightRef.current = true;
      try {
        const res = await fetch(`${API_URL}/auth/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${activeToken}`
          }
        });
        if (!res.ok) throw new Error('Failed to refresh session');
        const body = await res.json().catch(() => ({}));
        if (!body?.token) throw new Error('Missing token');
        if (cancelled) return;
        setAuthToken(body.token);
        setAuth(prev => {
          const nextUser = body.user ? { ...(prev.user || {}), ...body.user } : prev.user;
          return { ...prev, token: body.token, user: nextUser };
        });
        if (body.user) {
          setStoredUser(body.user);
        }
        scheduleTokenRefresh(body.token);
      } catch (err) {
        if (!cancelled) doLogout('expired');
      } finally {
        refreshInFlightRef.current = false;
      }
    }

    clearTimers();
    unbindActivity();

    if (!auth.token) {
      return () => {
        cancelled = true;
        clearTimers();
        unbindActivity();
      };
    }

    const evs = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    evs.forEach(ev => window.addEventListener(ev, resetIdle));
    activityBoundRef.current = true;
    resetIdle();
    scheduleTokenRefresh();

    return () => {
      cancelled = true;
      clearTimers();
      unbindActivity();
    };
  }, [auth.token, sessionTimeoutMs]);



  const promptDuplicateMode = ({ duplicates }) => {
    if (typeof document === 'undefined' || !Array.isArray(duplicates) || duplicates.length === 0) {
      return Promise.resolve('skip');
    }
    const plural = duplicates.length > 1;
    const preview = duplicates.slice(0, 3).map(f => f?.name || '').filter(Boolean).join(', ');
    const extra = duplicates.length > 3 ? ', …' : '';
    const baseMsg = plural
      ? `Some selected files already exist${preview ? ` (${preview}${extra})` : ''}.`
      : `The file "${duplicates[0].name}" already exists.`;
    return new Promise((resolve) => {
      let resolved = false;
      const cleanup = (result) => {
        if (resolved) return;
        resolved = true;
        document.removeEventListener('keydown', onKeyDown);
        try {
          if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
        } catch (_) {}
        resolve(result);
      };
      const overlay = document.createElement('div');
      overlay.style.position = 'fixed';
      overlay.style.inset = '0';
      overlay.style.background = 'rgba(0,0,0,0.45)';
      overlay.style.display = 'flex';
      overlay.style.alignItems = 'center';
      overlay.style.justifyContent = 'center';
      overlay.style.zIndex = '9999';
      overlay.style.padding = '16px';
      const modal = document.createElement('div');
      modal.style.background = 'var(--card-bg, #fff)';
      modal.style.color = 'var(--text, #000)';
      modal.style.borderRadius = '12px';
      modal.style.boxShadow = '0 16px 48px rgba(0,0,0,0.35)';
      modal.style.maxWidth = '420px';
      modal.style.width = '100%';
      modal.style.padding = '24px';
      modal.style.fontFamily = 'inherit';
      const title = document.createElement('h3');
      title.textContent = plural ? 'Duplicate files detected' : 'Duplicate file detected';
      title.style.marginTop = '0';
      title.style.marginBottom = '8px';
      const desc = document.createElement('p');
      desc.textContent = `${baseMsg} Choose an action:`;
      desc.style.marginTop = '0';
      desc.style.marginBottom = '16px';
      const btnRow = document.createElement('div');
      btnRow.style.display = 'flex';
      btnRow.style.flexDirection = 'column';
      btnRow.style.gap = '8px';
      const buttons = [
        {
          key: 'r',
          label: plural ? 'Replace existing files (R)' : 'Replace existing file (R)',
          value: 'replace',
          className: 'btn btn-delete'
        },
        {
          key: 'k',
          label: plural ? 'Keep all duplicates (K)' : 'Keep both files (K)',
          value: 'keep',
          className: 'btn btn-primary'
        },
        {
          key: 'c',
          label: plural ? 'Cancel uploading (C)' : 'Cancel upload (C)',
          value: 'cancel',
          className: 'btn'
        }
      ];
      buttons.forEach(({ key, label, value, className }) => {
        const btn = document.createElement('button');
        btn.textContent = label;
        btn.className = className || 'btn';
        btn.style.width = '100%';
        btn.style.justifyContent = 'center';
        btn.addEventListener('click', () => cleanup(value));
        btnRow.appendChild(btn);
      });
      const onKeyDown = (e) => {
        const key = (e.key || '').toLowerCase();
        if (key === 'r') { e.preventDefault(); cleanup('replace'); }
        else if (key === 'k') { e.preventDefault(); cleanup('keep'); }
        else if (key === 'c' || key === 'escape') { e.preventDefault(); cleanup('cancel'); }
      };
      document.addEventListener('keydown', onKeyDown);
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) cleanup('cancel');
      });
      modal.appendChild(title);
      modal.appendChild(desc);
      modal.appendChild(btnRow);
      overlay.appendChild(modal);
      document.body.appendChild(overlay);
      // Focus first button for accessibility
      setTimeout(() => {
        try { btnRow.querySelector('button')?.focus(); } catch (_) {}
      }, 0);
    });
  };

  // Handle single or multiple file upload
  const handleUpload = async () => {
    // Build a sequential list of files to upload
    const filesToUpload = [];
    if (file) filesToUpload.push(file);
    if (files && files.length) filesToUpload.push(...files);

    if (!filesToUpload.length) {
      alert('Please choose at least one PDF to upload.');
      return;
    }

    const existingLower = new Set(
      (Array.isArray(fileList) ? fileList : []).map(f => String(f?.filename || '').toLowerCase())
    );
    const duplicateCandidates = filesToUpload.filter(f => existingLower.has(String(f?.name || '').toLowerCase()));
    let duplicateMode = 'skip';
    if (duplicateCandidates.length) {
      duplicateMode = await promptDuplicateMode({ duplicates: duplicateCandidates });
      if (duplicateMode === 'cancel') {
        try { showToast('Upload cancelled.', 'info', 3000); } catch (_) {}
        return;
      }
    }

    setUploadTotal(filesToUpload.length);
    setUploadIndex(0);
    setUploading(true);
    setUploadOverallProgress(0);
    setUploadEta('');
    try { showToast(`Uploading ${filesToUpload.length} file${filesToUpload.length === 1 ? '' : 's'}…`, 'info', 0); } catch (_) {}

    // Pre-compute total bytes for ETA estimation
    const totalBytes = filesToUpload.reduce((sum, f) => sum + (f?.size || 0), 0);
    let bytesDone = 0; // sum of fully uploaded prior files
    const startTs = Date.now();

    let successCount = 0;
    const uploadedNames = [];
    let aborted = false;
    for (let i = 0; i < filesToUpload.length; i++) {
      const f = filesToUpload[i];
      setUploadIndex(i + 1);
      setUploadName(f.name);
      setUploadProgress(0);

      const formData = new FormData();
      // Send as single file field supported by backend
      formData.append('pdf', f);

      const source = axios.CancelToken.source();
      setCancelToken(source);

      try {
        const axiosConfig = {
          onUploadProgress: (progressEvent) => {
            const loaded = Math.max(0, progressEvent.loaded || 0);
            const totalForEvent = Math.max(1, progressEvent.total || f.size || 1);
            const percent = Math.min(100, Math.round((loaded * 100) / totalForEvent));
            setUploadProgress(percent);
            // Overall progress combines completed files + fraction of current
            const fraction = percent / 100;
          const overall = uploadTotal > 0 ? Math.round(((i + fraction) / uploadTotal) * 100) : percent;
          setUploadOverallProgress(overall);
            try { showToast(`Uploading… ${overall}%`, 'info', 0); } catch (_) {}

            // ETA: estimate based on total bytes and elapsed throughput
            if (totalBytes > 0) {
              const uploadedSoFar = bytesDone + loaded;
              const elapsedSec = Math.max(0.001, (Date.now() - startTs) / 1000);
              const bytesPerSec = uploadedSoFar / elapsedSec;
              if (bytesPerSec > 0) {
                const remainingBytes = Math.max(0, totalBytes - uploadedSoFar);
                const etaSec = Math.round(remainingBytes / bytesPerSec);
                const mins = Math.floor(etaSec / 60);
                const secs = etaSec % 60;
                const etaStr = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
                setUploadEta(etaStr);
              }
            }
          },
          cancelToken: source.token,
        };
        if (duplicateMode !== 'skip') {
          axiosConfig.headers = { 'X-Duplicate-Mode': duplicateMode };
        }
        const response = await axios.post(`${API_URL}/upload`, formData, axiosConfig);
        successCount += 1;
        try {
          const data = response?.data || {};
          const storedName = (Array.isArray(data.uploaded) && data.uploaded[0])
            ? data.uploaded[0]
            : (data.filename || f?.name || '');
          uploadedNames.push(storedName);
        } catch (_) {}
        // Snap overall to the boundary for completed file
        if (uploadTotal) {
          const overall = Math.round(((i + 1) / uploadTotal) * 100);
          setUploadOverallProgress(overall);
        }
        // Advance bytesDone for ETA base
        bytesDone += f?.size || 0;
      } catch (err) {
        if (axios.isCancel(err)) {
          console.log('Upload cancelled');
          aborted = true;
          break;
        } else {
          console.error('Upload failed for', f?.name, err);
          // Stop on first hard failure to keep feedback simple
          aborted = true;
          break;
        }
      } finally {
        setCancelToken(null);
      }
    }

    if (!aborted && successCount === filesToUpload.length) {
      setFiles([]);
      setFile(null);
      const msg = uploadedNames.length === 1
        ? `Uploaded ${uploadedNames[0]}`
        : `Uploaded ${uploadedNames.length} files`;
      showToast(msg, 'success');
    } else if (aborted) {
      showToast('Upload cancelled or failed.', 'error', 3500);
    }

    // Reset UI state
    setUploading(false);
    setUploadProgress(0);
    setUploadName('');
    setUploadIndex(0);
    setUploadTotal(0);
    setUploadOverallProgress(0);
    setUploadEta('');
  }; // handleUpload



  // Cancel the upload if needed
  const cancelUpload = () => {
    if (cancelToken) {
      cancelToken.cancel('Upload cancelled by user');
    }
  };



  const { getRootProps, getInputProps } = useDropzone({
    accept: { 'application/pdf': ['.pdf'] },
    onDrop: (acceptedFiles) => setFiles(acceptedFiles),
    multiple: true, // Allow multiple files
  });



  const handleSearch = async () => {
    const terms = query.split('+').map(term => term.trim().toLowerCase()).filter(Boolean);

    if (terms.length === 0) {
        alert('Please enter some search terms.');
        return;
    }

    const source = axios.CancelToken.source();
    setSearchCancelToken(source); // store it
    setSearching(true);

    try {
        try { showToast('Searching…', 'info', 0); } catch (_) {}
        const res = await axios.get(`${API_URL}/search?q=` + terms.join(' '), {
        cancelToken: source.token
        });
        const arr = Array.isArray(res.data) ? res.data : [];
        setResults(arr);
        setHasSearched(true);
        try { showToast(`Found ${arr.length} result${arr.length === 1 ? '' : 's'}`, 'success', 2000); } catch (_) {}
    } catch (err) {
        if (axios.isCancel(err)) {
        console.log('Search cancelled');
        try { showToast('Search cancelled', 'info', 2000); } catch (_) {}
        } else {
        console.error('Search failed:', err);
        try { showToast('Search failed', 'error', 3500); } catch (_) {}
        }
    } finally {
        setSearching(false);
        setSearchCancelToken(null);
    }
}; // handlesearch

  // Clear the search input and results
  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setHasSearched(false);
  };



const handleSearchSort = (field) => {
  const direction =
    searchSortField === field && searchSortDirection === 'asc'
      ? 'desc'
      : 'asc';

  setSearchSortField(field);
  setSearchSortDirection(direction);
};


  const loadingFilesRef = useRef(false);
  const loadFilesSeqRef = useRef(0);
  const loadFiles = async (opts = {}) => {
    const silent = !!opts.silent;
    const force = !!opts.force;
    const refresh = opts.refresh !== undefined ? !!opts.refresh : force;
    if (loadingFilesRef.current && !force) {
      // Avoid overlapping loads and duplicate toasts
      return;
    }
    const seq = loadFilesSeqRef.current + 1;
    loadFilesSeqRef.current = seq;
    loadingFilesRef.current = true;
    try {
      if (!silent) { try { showToast('Loading files…', 'info', 0); } catch (_) {} }
      const params = refresh ? { refresh: '1', _: Date.now() } : { _: Date.now() };
      const res = await axios.get(`${API_URL}/files`, {
        params,
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });
      const list = Array.isArray(res.data) ? res.data : [];
      if (seq !== loadFilesSeqRef.current) return;
      setFileList(list);
      if (!silent) {
        try {
          const repaired = parseInt((res.headers && (res.headers['x-repair-writes'] || res.headers['X-Repair-Writes'])) || '0', 10) || 0;
          if (repaired > 0) {
            showToast(`Repaired ${repaired} file${repaired === 1 ? '' : 's'} this pass`, 'info', 2000);
          } else {
            showToast(`Loaded ${list.length} file${list.length === 1 ? '' : 's'}`, 'success', 1200);
          }
        } catch (_) {}
      }
    } catch (err) {
      console.error('Failed to load files:', err);
      if (!silent) { try { showToast('Failed to load files', 'error', 3500); } catch (_) {} }
    } finally {
      if (seq === loadFilesSeqRef.current) {
        loadingFilesRef.current = false;
      }
    }
  };

  const isAdmin = Number(auth?.user?.role) === 1;
  const userRights = Array.isArray(auth?.user?.rights)
    ? auth.user.rights.map(Number).filter(Number.isInteger)
    : [];
  const canUpload = isAdmin || userRights.includes(1);
  const canDeleteFiles = isAdmin;
  const hasUser = !!auth?.user;
  const nonAdminDefaultView = 'all';
  const fallbackView = canUpload ? 'upload' : nonAdminDefaultView;
  const isAdminView = String(view || '').startsWith('admin-');
  const restrictedView = (!isAdmin && isAdminView)
    || (!canUpload && view === 'upload')
    || (!isAdmin && view === 'trash');
  const safeView = restrictedView
    ? fallbackView
    : (view || fallbackView);

  const handleDelete = async (filename) => {
    if (!canDeleteFiles) {
      showToast('Only admins can delete files', 'error', 3500);
      throw new Error('Forbidden');
    }
    try {
      await axios.delete(`${API_URL}/files/` + filename);
      setFileList(prev => prev.filter(f => f.filename !== filename));
      showToast(`Moved "${filename}" to trash`, 'success', 2500);
    } catch (err) {
      console.error('Failed to delete file:', err);
      showToast('Failed to delete file', 'error', 3500);
    }
  };

  const parseFilename = (filename) => {
    const base = filename.toLowerCase().endsWith('.pdf') ? filename.slice(0, -4) : filename;
    const parts = base.split('_');
    const obj = {};
    for (let i = 0; i < fieldKeys.length; i++) {
      obj[fieldKeys[i]] = parts[i] || '';
    }
    return obj;
  };

  const handleSort = (field) => {
    const direction = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(direction);
  };

  // Persist sort selection for All Files table
  useEffect(() => {
    try {
      if (sortField) localStorage.setItem('allFiles.sortField', sortField);
      if (sortDirection) localStorage.setItem('allFiles.sortDirection', sortDirection);
    } catch (_) {}
  }, [sortField, sortDirection]);

  // Persist the current view so refresh restores it
  useEffect(() => {
    try { localStorage.setItem('app.view', view); } catch (_) {}
  }, [view]);

  // Once the user is known, keep them out of views they do not have permission to use
  useEffect(() => {
    if (!hasUser) return;
    const cannotUseView = (!isAdmin && isAdminView)
      || (!canUpload && view === 'upload')
      || (!isAdmin && view === 'trash');
    if (cannotUseView) {
      setView(fallbackView);
    }
  }, [hasUser, isAdmin, canUpload, isAdminView, view, fallbackView]);

  // Hide deprecated/duplicate 'manage' view by redirecting to 'all'
  useEffect(() => {
    if (view === 'manage') {
      setView('all');
    }
  }, [view]);

  // When entering the All Files view, hydrate sort state from localStorage
  useEffect(() => {
    if (view !== 'all') return;
    try {
      const sf = localStorage.getItem('allFiles.sortField');
      const sd = localStorage.getItem('allFiles.sortDirection');
      if (sf) setSortField(sf);
      if (sd === 'asc' || sd === 'desc') setSortDirection(sd);
    } catch (_) {}
  }, [view]);

  const getSortedFiles = () => {
    const sorted = [...fileList];
    const sf = sortField && fieldKeys.includes(sortField) ? sortField : (fieldKeys[0] || null);
    if (!sf) return sorted;

    sorted.sort((a, b) => {
      const aVal = String(parseFilename(a.filename)[sf] || '').toLowerCase();
      const bVal = String(parseFilename(b.filename)[sf] || '').toLowerCase();
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  useEffect(() => {
    if (!auth.token) return;
    if (view === 'manage' || view === 'all') {
      loadFiles({ force: true });
    }
    (async () => {
      try {
        const res = await authFetch(`${API_URL}/cabinets/active`);
        if (res.ok) {
          const active = await res.json();
          if (active && active.name) {
            setCustomerName(active.name);
            try { localStorage.setItem('customerName', String(active.name)); } catch (_) {}
          }
          const raw = String(active?.fields || '').trim();
          const labels = (raw ? raw.split(';') : []).map(s => s.trim()).filter(Boolean);
          const usedLabels = labels.length ? labels : ['File #', 'Buyer Name', 'Seller Name'];
          const keys = usedLabels.map(l => l.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, ''));
          setCabinetFields(usedLabels);
          setFieldKeys(keys);
          setSortField(prev => (keys.includes(prev) ? prev : (keys[0] || null)));
        }
      } catch (e) {
        // ignore
      }
    })();
  }, [view, auth.token]);

  // Poll OCR status to drive subtle header progress
  useEffect(() => {
    if (!auth.token) return; // no polling when logged out
    let timer;
    const prev = { active: false, processed: 0 };
    const poll = async () => {
      try {
        const pauseUI = typeof window !== 'undefined' && (window.spartaPauseUIUpdates === true);
        if (pauseUI) return; // skip state updates that can collapse open dropdowns
        const res = await authFetch(`${API_URL}/ocr/status`);
        const json = await res.json();
        // Skip setting state while Admin Status editor is active to avoid re-renders
        if (!(typeof window !== 'undefined' && window.spartaPauseUIUpdates === true)) {
          setOcrStatus(json);
        }
        // If OCR just processed a file or finished, refresh file list in views
        const processedChanged = typeof json?.processed === 'number' && json.processed !== prev.processed;
        const finishedNow = prev.active && !json?.active;
        if (!pauseUI && (processedChanged || finishedNow) && (view === 'manage' || view === 'all')) {
          try { await loadFiles({ silent: true }); } catch (_) {}
        }
        // Avoid disruptive toasts while editing settings on Admin Status
        const pauseToasts = typeof window !== 'undefined' && window.spartaPauseToasts === true;
        if (!pauseToasts) {
          prev.active = !!json?.active;
          prev.processed = Number(json?.processed || 0);
        } else {
          // Still track state but don't emit toasts; just update counters silently
          prev.active = !!json?.active;
          prev.processed = Number(json?.processed || 0);
        }
      } catch (e) {
        // ignore polling errors; keep last known status
      }
    };
    poll();
    timer = setInterval(poll, 1500);
    return () => clearInterval(timer);
  }, [auth.token, view]);

  // Poll Repair status and surface toasts
  useEffect(() => {
    if (!auth.token) return;
    let timer;
    const prev = { active: false, processed: 0 };
    const poll = async () => {
      try {
        const res = await authFetch(`${API_URL}/repair/status`);
        const json = await res.json();
        const active = !!json?.active;
        const processed = Number(json?.processed || 0);
        const total = Number(json?.total || 0);
        const pauseToasts = typeof window !== 'undefined' && window.spartaPauseToasts === true;
        if (!pauseToasts) {
          if (active && !prev.active) {
            try { showToast(`Repairing${total ? ` ${Math.min(processed+1,total)}/${total}` : ''}…`, 'info', 0); } catch (_) {}
          } else if (active && (processed !== prev.processed)) {
            try { showToast(`Repairing ${Math.min(processed+1,total)}/${total}`, 'info', 0); } catch (_) {}
          } else if (!active && prev.active) {
            try { showToast('Repair complete', 'success', 2500); } catch (_) {}
          }
        }
        prev.active = active;
        prev.processed = processed;
      } catch (e) {
        // ignore polling errors
      }
    };
    poll();
    timer = setInterval(poll, 1500);
    return () => clearInterval(timer);
  }, [auth.token, view]);

  // Initialize auth from localStorage
  useEffect(() => {
    const token = getToken();
    if (token) {
      applyAxiosAuth(token);
      const cachedUser = getStoredUser();
      setAuth({ token, user: cachedUser });
      // Hydrate user profile (for theme, name, etc.)
      (async () => {
        try {
          const res = await authFetch(`${API_URL}/auth/me`);
          if (res.ok) {
            const user = await res.json();
            setAuth(a => ({ ...a, user }));
            setStoredUser(user);
          }
        } catch (_) {}
      })();
    }
  }, []);

  const handleLoginSuccess = (token, user) => {
    setAuthToken(token);
    applyAxiosAuth(token);
    setAuth({ token, user });
    setStoredUser(user);
    // Schedule greeting after login
    try { greetedRef.current = false; scheduleGreeting(); } catch (_) {}
  };

  const handleSaveUserSettings = async ({ timeoutMinutes, currentPassword, newPassword }) => {
    setSettingsBusy(true);
    setSettingsError('');
    try {
      const payload = { timeout: timeoutMinutes };
      if (newPassword) {
        payload.newPassword = newPassword;
        payload.currentPassword = currentPassword || '';
      }
      const res = await authFetch(`${API_URL}/me/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body?.ok !== true) {
        throw new Error(body.message || 'Failed to save settings');
      }
      if (body.user) {
        setAuth(prev => ({ ...prev, user: { ...(prev.user || {}), ...body.user } }));
        setStoredUser(body.user);
      }
      if (body.token) {
        setAuthToken(body.token);
        applyAxiosAuth(body.token);
        setAuth(prev => ({ ...prev, token: body.token }));
      }
      setSettingsOpen(false);
      setSettingsError('');
      showToast('Settings updated', 'success', 2500);
    } catch (e) {
      setSettingsError(e.message || 'Failed to save settings');
    } finally {
      setSettingsBusy(false);
    }
  };

  // Apply theme to document whenever effective theme changes, and persist to localStorage
  useEffect(() => {
    const theme = (uiTheme || 'light').toLowerCase();
    document.documentElement.setAttribute('data-theme', theme === 'dark' ? 'dark' : 'light');
    try { localStorage.setItem('theme', theme); } catch (_) {}
  }, [uiTheme]);

  // Update browser tab title and persist cabinet name when it changes
  useEffect(() => {
    const name = (customerName || '').trim();
    try { localStorage.setItem('customerName', name); } catch (_) {}
    document.title = name || (import.meta.env.VITE_COMPANY_NAME || 'TrueView Docs');
  }, [customerName]);

  // Keep uiTheme in sync with user profile theme when it changes (e.g., after login or server update)
  // Only apply if the server actually provides a theme; don't overwrite local choice with a default.
  useEffect(() => {
    if (!auth?.user?.theme) return;
    const theme = String(auth.user.theme).toLowerCase();
    setUiTheme(theme);
    try { localStorage.setItem('theme', theme); } catch (_) {}
  }, [auth?.user?.theme]);

  // Global handler for unauthorized responses -> force logout and notify
  useEffect(() => {
    const onUnauthorized = () => {
      try { clearToken(); } catch(_) {}
      setAuth({ token: '', user: null });
      showToast('Session expired. Please sign in again.', 'info', 5000);
    };
    window.addEventListener('sparta:unauthorized', onUnauthorized);
    return () => window.removeEventListener('sparta:unauthorized', onUnauthorized);
  }, []);

  // render
  const renderContent = () => {
    if (safeView === 'upload') {
      return (
        <UploadView
            file={file}
            setFile={setFile}
            files={files}
            setFiles={setFiles}
            handleUpload={handleUpload}
            uploading={uploading}
            cancelUpload={cancelUpload}
            uploadProgress={uploadProgress}
            uploadName={uploadName}
            uploadIndex={uploadIndex}
            uploadTotal={uploadTotal}
            uploadOverallProgress={uploadOverallProgress}
            uploadEta={uploadEta}
            cancelToken={cancelToken}
            API_URL={API_URL}
            fieldLabels={cabinetFields}
            fieldKeys={fieldKeys}
            />
      );
    }

    if (safeView === 'barcode') {
        return (
            <NewBarcodeView setView={setView} fields={cabinetFields} />
        );
    }

    if (safeView === 'search') {
        return (
            <SearchView
            query={query}
            setQuery={setQuery}
            handleSearch={handleSearch}
            clearSearch={clearSearch}
            results={results}
            hasSearched={hasSearched}
            selectedRow={selectedRow}
            setSelectedRow={setSelectedRow}
            parseFilename={parseFilename}
            fieldLabels={cabinetFields}
            fieldKeys={fieldKeys}
            API_URL={API_URL}
            onEmail={(filename) => { setEmailFilename(filename); setEmailModalOpen(true); }}
            />
        );
    }

    if (safeView === 'manage') {
        return (
            <ManageView
            fileList={fileList}
            handleDelete={handleDelete}
            parseFilename={parseFilename}
            fieldLabels={cabinetFields}
            fieldKeys={fieldKeys}
            sortField={sortField}
            sortDirection={sortDirection}
            handleSort={handleSort}
            API_URL={API_URL}
            selectedRow={selectedRow}
            setSelectedRow={setSelectedRow}
            canDelete={canDeleteFiles}
            onEmail={(filename) => { setEmailFilename(filename); setEmailModalOpen(true); }}
            />
        );
    }
    
    if (safeView === 'all') {
        return (
            <AllFilesView
            fileList={fileList}
            parseFilename={parseFilename}
            fieldLabels={cabinetFields}
            fieldKeys={fieldKeys}
            sortField={sortField}
            sortDirection={sortDirection}
            handleSort={handleSort}
            API_URL={API_URL}
            selectedRow={selectedRow}
            setSelectedRow={setSelectedRow}
            canDelete={canDeleteFiles}
            onDelete={handleDelete}
            onEmail={(filename) => { setEmailFilename(filename); setEmailModalOpen(true); }}
            isAdmin={isAdmin}
            />
        );
    }

    if (safeView === 'trash') {
        if (!isAdmin) {
            return (
              <div style={{ padding: 24 }}>
                <h2>Restricted</h2>
                <p style={{ maxWidth: 420 }}>Only administrators can view the Trash. If you believe this is an error, please contact your administrator.</p>
              </div>
            );
        }
        return (
            <TrashView
              API_URL={API_URL}
              onChange={async () => {
                try { await loadFiles({ silent: true }); } catch (_) {}
              }}
            />
        );
    }

    if (safeView === 'admin-users') {
        return (
            <UsersView
              API_URL={API_URL}
            />
        );
    }
    if (safeView === 'admin-status') {
        return (
            <AdminStatusView
              API_URL={API_URL}
            />
        );
    }
    if (safeView === 'admin-cabinets') {
        return (
            <AdminCabinetsView
              API_URL={API_URL}
            />
        );
    }
    if (safeView === 'admin-backup') {
        return (
            <AdminBackupView
              API_URL={API_URL}
            />
        );
    }
  }; // render content

    const username = "John Doe";

    

    if (!auth.token) {
      return <LoginView API_URL={API_URL} onSuccess={handleLoginSuccess} />;
    }

    return (
        <div className="app-container">
            {/* Fixed Top Header */}
            <TopHeader
              customerName={customerName}
              username={(auth?.user && (`${auth.user.first_name || ''} ${auth.user.last_name || ''}`.trim() || auth.user.login)) || 'User'}
              ocrStatus={ocrStatus}
              theme={(uiTheme || 'light').toLowerCase()}
              onOpenSettings={() => { setSettingsError(''); setSettingsOpen(true); }}
              onToggleTheme={async () => {
                try {
                  const current = (uiTheme || 'light').toLowerCase();
                  const nextTheme = current === 'dark' ? 'light' : 'dark';
                  // Optimistically apply theme for instant feedback
                  setUiTheme(nextTheme);
                  document.documentElement.setAttribute('data-theme', nextTheme);
                  try { localStorage.setItem('theme', nextTheme); } catch (_) {}
                  const res = await authFetch(`${API_URL}/me/theme`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ theme: nextTheme })
                  });
                  if (!res.ok) throw new Error('Failed to update theme');
                  setAuth(a => ({ ...a, user: { ...(a.user || {}), theme: nextTheme } }));
                } catch (e) {
                  console.error(e);
                }
              }}
              onLogout={() => {
                clearToken();
                setAuth({ token: '', user: null });
              }}
              onOpenCabinets={() => { setCabinetSwitchingName(''); setCabinetsOpen(true); }}
            />

            {searching && (
                <div className="upload-spinner-overlay">
                    <div className="upload-spinner-box">
                    <div className="spinner-large"></div>
                    <p>Searching, please wait...</p>
                    <button
                        onClick={() => {
                        if (searchCancelToken) {
                            searchCancelToken.cancel('User cancelled search');
                        }
                        }}
                        className="btn btn-delete"
                        style={{ marginTop: '10px' }}
                    >
                        Cancel Search
                    </button>
                    </div>
                </div>
            )}

            {/* Main layout (pushed down by 60px header) */}
            <div className="app-main">
                {/* Sidebar */}
                <SidebarMenu view={safeView} setView={setView} isAdmin={isAdmin} canUpload={canUpload} />

                {/* Right-side Work Area */}
                <div id="work" className="app-work">

                    {renderContent()}
                </div>
            </div>

            <EmailModal
              open={emailModalOpen}
              filename={emailFilename}
              onClose={() => { if (!emailBusy) setEmailModalOpen(false); }}
              busy={emailBusy}
              busyText={emailBusyText}
              onSend={async ({ to, cc, bcc, subject, message, pagesMode, pageRange }) => {
                try {
                  setEmailBusyText('Preparing email…');
                  setEmailBusy(true);
                  const body = {
                    to, cc, bcc, subject, message,
                    pagesMode: pagesMode === 'range' ? 'range' : 'all',
                    pageRange
                  };
                  setEmailBusyText('Sending email…');
                  const res = await authFetch(`${API_URL}/files/${encodeURIComponent(emailFilename)}/email`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body)
                  });
                  if (!res.ok) {
                    const j = await res.json().catch(() => ({}));
                    throw new Error(j.message || 'Failed to send email');
                  }
                  alert('Email sent');
                  setEmailModalOpen(false);
                } catch (e) {
                  alert(e.message || 'Failed to send email');
                } finally {
                  setEmailBusy(false);
                }
              }}
            />
            <UserSettingsModal
              open={settingsOpen}
              onClose={closeSettings}
              onSave={handleSaveUserSettings}
              initialTimeout={Number(auth?.user?.timeout) || 30}
              saving={settingsBusy}
              error={settingsError}
            />
            <CabinetsModal
              open={cabinetsOpen}
              API_URL={API_URL}
              switchingName={cabinetSwitchingName}
              onClose={() => {
                if (cabinetSwitchingName) return;
                setCabinetsOpen(false);
              }}
              onSelect={async (cab) => {
                const selectedName = String(cab?.name || '').trim() || 'cabinet';
                try {
                  setCabinetSwitchingName(selectedName);
                  const res = await authFetch(`${API_URL}/cabinets/${cab.id}/select`, { method: 'POST' });
                  if (!res.ok) {
                    const j = await res.json().catch(() => ({}));
                    throw new Error(j.message || 'Failed to switch cabinet');
                  }
                  const payload = await res.json().catch(() => ({}));
                  const active = payload?.active || { name: cab.name, fields: '' };
                  const nextName = active.name || cab.name;
                  setCustomerName(nextName);
                  try { localStorage.setItem('customerName', String(nextName)); } catch (_) {}
                  const raw = String(active?.fields || '').trim();
                  const labels = (raw ? raw.split(';') : []).map(s => s.trim()).filter(Boolean);
                  const usedLabels = labels.length ? labels : ['File #', 'Buyer Name', 'Seller Name'];
                  const keys = usedLabels.map(l => l.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, ''));
                  setCabinetFields(usedLabels);
                  setFieldKeys(keys);
                  setSortField(keys[0] || null);
                  // Reload files list to reflect the new cabinet
                  await loadFiles({ force: true });
                } catch (e) {
                  alert(e.message || 'Failed to switch cabinet');
                } finally {
                  setCabinetSwitchingName('');
                  setCabinetsOpen(false);
                }
              }}
            />

            {/* Toast (non-blocking) */}
            {toast.open && (
              <div style={toastContainerStyle}>
                <div className="sp-toast" style={{
                  ...toastStyleBase,
                  backgroundColor: toastColors[toast.type] || toastColors.info,
                }}>
                  <span style={{ marginRight: 12 }}>{toast.message}</span>
                  <button
                    onClick={() => setToast(t => ({ ...t, open: false }))}
                    className="btn"
                    style={{ backgroundColor: 'rgba(0,0,0,0.2)', padding: '4px 8px' }}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
        </div>
    );
} // app

// === Styles ===
const headerStyle = {
  width: '100vw',
  backgroundColor: '#0f2027',
  color: '#fff',
  padding: '10px 20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: '1px solid #333',
  position: 'sticky',
  top: 0,
  zIndex: 1000,
  boxSizing: 'border-box',
  height: '60px',
}
const input = {
  padding: '8px 12px',
  backgroundColor: '#1e1e1e',
  color: '#fff',
  border: '1px solid #333',
  borderRadius: 4,
};

const button = {
  marginLeft: 10,
  padding: '8px 14px',
  backgroundColor: '#1e88e5',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
};

const deleteButton = {
  marginLeft: 10,
  padding: '6px 10px',
  backgroundColor: '#d32f2f',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
};

const clearButton = {
  marginLeft: 10,
  padding: '6px 10px',
  backgroundColor: '#d32f2f',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
};

const table = {
  width: '100%',
  borderCollapse: 'collapse',
  marginTop: 20,
};

const th = {
  textAlign: 'left',
  padding: '12px 10px',
  backgroundColor: '#1e1e1e',
  color: '#90caf9',
  borderBottom: '2px solid #333',
  fontSize: '16px', // Increased font size
  fontWeight: 'bold'
};


const td = {
  padding: '10px',
  borderBottom: '1px solid #333',
  verticalAlign: 'top',
};

const rowEven = { backgroundColor: '#1a1a1a' };
const rowOdd = { backgroundColor: '#141414' };

const link = {
  color: '#64b5f6',
  textDecoration: 'none',
  marginRight: 10,
};

const viewButton = {
  padding: '6px 12px',
  backgroundColor: '#1565c0',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
  textDecoration: 'none',
  display: 'inline-block',
  transition: 'background-color 0.2s',
};



export default App;

// Lightweight inline styles for toast
const toastContainerStyle = {
  position: 'fixed',
  right: 16,
  top: 70, // just below 60px header
  zIndex: 3000,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};
const toastStyleBase = {
  minWidth: 240,
  maxWidth: 420,
  padding: '10px 12px',
  borderRadius: 8,
  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};
const toastColors = {
  success: '#2e7d32',
  info: '#1565c0',
  error: '#c62828',
};
